import { User, Calendar, RefreshCw, Settings } from 'lucide-react'
import { useTimesConfiguracao } from '../hooks/useTime'
import { TimeConfiguracao } from '../tipos/time'

const CORES_CARDS = [
  { header: 'bg-brand-500', headerText: 'text-white', badge: 'bg-brand-50 text-brand-700 border-brand-100' },
  { header: 'bg-emerald-600', headerText: 'text-white', badge: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
  { header: 'bg-violet-600', headerText: 'text-white', badge: 'bg-violet-50 text-violet-700 border-violet-100' },
  { header: 'bg-amber-500', headerText: 'text-white', badge: 'bg-amber-50 text-amber-700 border-amber-100' },
  { header: 'bg-rose-500', headerText: 'text-white', badge: 'bg-rose-50 text-rose-700 border-rose-100' },
  { header: 'bg-sky-600', headerText: 'text-white', badge: 'bg-sky-50 text-sky-700 border-sky-100' },
] as const

function formatarData(dataCriacao: string | null): string {
  if (!dataCriacao) return '—'
  const d = new Date(dataCriacao)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function CardTime({ time, cor }: { time: TimeConfiguracao; cor: (typeof CORES_CARDS)[number] }) {
  return (
    <article className="rounded-lg border border-gray-200 shadow-theme-xs overflow-hidden bg-white transition-all duration-200 hover:shadow-theme-sm hover:border-gray-300">
      <header className={`${cor.header} ${cor.headerText} px-4 py-3`}>
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold truncate" title={time.descricao}>
            {time.descricao}
          </h3>
          {time.externo && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white/25 text-white flex-shrink-0">
              Externo
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-1.5 text-white/90 text-xs">
          <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
          <span>Criado em {formatarData(time.dataCriacao)}</span>
        </div>
      </header>
      <div className="px-4 py-3">
        <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-2">Integrantes</p>
        {time.integrantes.length === 0 ? (
          <p className="text-xs text-gray-500 italic">Nenhum integrante vinculado</p>
        ) : (
          <ul className="space-y-1.5">
            {time.integrantes.map((int, idx) => (
              <li
                key={idx}
                className={`flex items-center gap-2 text-xs py-1.5 px-2.5 rounded-lg border ${cor.badge}`}
              >
                <User className="w-3.5 h-3.5 flex-shrink-0 opacity-80" />
                <span className="font-medium truncate" title={int.nome}>
                  {int.nome}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </article>
  )
}

export default function ConfiguracaoTimes() {
  const { data: times = [], isLoading, refetch } = useTimesConfiguracao()

  const timesVisiveis = times.filter(
    (time) => time.descricao.trim().toLowerCase() !== 'momentum'
  )

  if (isLoading) {
    return (
      <div className="flex flex-col">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Configuração de Times</h1>
            <p className="mt-1 text-sm text-gray-600">Visualize todos os times e seus integrantes</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm p-12 text-center">
          <div className="loading-spinner mx-auto mb-4 w-10 h-10" />
          <p className="text-gray-600">Carregando times...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configuração de Times</h1>
          <p className="mt-1 text-sm text-gray-600">Visualize todos os times e seus integrantes</p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-theme-xs"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </button>
      </div>

      {timesVisiveis.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm overflow-hidden">
          <div className="p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 text-gray-400 mb-4">
              <Settings className="w-8 h-8" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum time cadastrado</h3>
            <p className="text-gray-600 max-w-sm mx-auto">
              Os times aparecerão aqui quando forem criados. Acesse Manutenção para cadastrar.
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6">
          {timesVisiveis.map((time, index) => (
            <CardTime key={time.id} time={time} cor={CORES_CARDS[index % CORES_CARDS.length]} />
          ))}
        </div>
      )}
    </div>
  )
}
